<template>
  <!-- eslint-disable vue/no-unused-vars -->
  <v-expansion-panel class="mb-5">
    <v-expansion-panel-header class="line-pricing-group pt-0 pb-0 pl-0" @click="preventExpand">
      <v-col cols="7">
        <v-text-field
          v-model="linePricingGroup.linePricedGroupDescription"
          class="pt-0 pb-0 highlight"
          :rules="rules.missingDescription"
          :disabled="disabled"
          @keyup.space="preventExpand"
        />
      </v-col>
      <v-col>
        <span class="float-right">
          <v-icon v-if="linePricingGroup.products.length && subGroupValidationError" color="red">
            mdi-alert-circle-outline
          </v-icon>
        </span>
      </v-col>
      <template v-slot:actions>
        <v-icon>expand_more</v-icon>
      </template>
    </v-expansion-panel-header>
    <v-expansion-panel-content class="line-pricing-group">
      <v-col cols="12" class="pt-0">
        <v-data-table
          class="line-priced-products products-table"
          :items="linePricingGroupProducts"
          :options.sync="pagination"
          item-key="_id"
          :sort-desc.sync="pagination.descending"
          hide-default-footer
        >
          <!-- Keep v-slot:header="props" so that header is accessible from the child -->
          <template v-if="linePricingGroup.products.length > 0" v-slot:header="props">
            <tr class="v-data-table-header">
              <!-- empty th above delete icon -->
              <th />
              <th
                v-for="header in headers"
                :key="header.text"
                :class="getHeaderClasses(header, pagination)"
                :style="header.style"
                class="column"
                @click="header.sortable ? changeSort(header.value) : ''"
              >
                {{ header.text }}
                <v-icon v-if="header.sortable" small class="v-data-table-header__icon">
                  arrow_upward
                </v-icon>
              </th>
            </tr>
          </template>
          <template v-slot:item="props">
            <tr :key="props.item._id" class="product-row">
              <td>
                <v-icon class="delete-icon" :disabled="disabled" @click="deleteProduct(props.item)">
                  delete
                </v-icon>
              </td>
              <td v-if="isManualAnchorEnabled" class="text-xs-center">
                <v-checkbox
                  v-model="props.item.isManualAnchor"
                  :disabled="props.item.isDisabledForManualAnchor"
                  :class="{ 'checkbox-disabled': props.item.isDisabledForManualAnchor }"
                  hide-details
                  class="manual-anchor-checkbox"
                  @change="updateManualAnchorStates(props.item)"
                />
              </td>
              <td class="text-xs-left">{{ props.item.productKeyDisplay }}</td>
              <td v-if="isFeatureFlagEnabled(useZones)" class="text-xs-left">
                <tooltipped-truncated-field
                  :text="storegroupKeyNameMap[props.item.toolStoreGroupKey]"
                  :truncation-length="truncationLength.toolStoreGroupDescription"
                />
              </td>
              <td class="text-xs-left text-bold description-cell">
                <tooltipped-truncated-field
                  :text="props.item.description"
                  :truncation-length="truncationLength.linePricedProducts"
                />
              </td>
              <td class="text-xs-left">{{ props.item.size }}</td>
              <td class="text-xs-center">{{ props.item.packageTypeDescription }}</td>
              <td class="text-xs-center">
                {{
                  formatNumber({
                    number: getIntentionCost(props.item),
                    format: numberFormats.costFormat,
                    nullAsDash: true,
                  })
                }}
              </td>
              <td class="text-xs-center">
                {{
                  formatNumber({
                    number: getLivePrice(props.item),
                    format: numberFormats.priceFormat,
                    nullAsDash: true,
                  })
                }}
              </td>
              <td>{{ props.item.subGroup }}</td>
              <td class="line-price-factor">
                <!-- <form novalidate> is required to disable browser native validation  -->
                <!-- if "step"="0.1" and a user enters 0.13, the following notification will be displayed:  -->
                <!-- please enter a valid value. the two nearest valid values are 0.1 and 0.2 -->
                <form novalidate>
                  <v-text-field
                    v-model.number="props.item.linePriceFactor"
                    :rules="rules.linePriceFactorRules"
                    :disabled="disabled"
                    type="number"
                    min="0.1"
                    step="0.1"
                    class="pt-0 pb-0 pr-2 highlight line-price-factor-input"
                  />
                </form>
              </td>
            </tr>
          </template>
          <template v-slot:no-data>
            {{ $t('settings.architecture.noDataLinePriceGroup') }}
          </template>
        </v-data-table>
      </v-col>
      <v-row>
        <v-col class="text-right pt-0">
          <span
            v-if="linePricingGroup.products.length && subGroupValidationError"
            class="validation-fail"
          >
            {{ $t('settings.architecture.differentSubGroupValidationError') }}
          </span>
          <v-btn
            class="delete-btn text-right"
            text
            icon
            :disabled="disabled"
            depressed
            @click="openDeleteDialog()"
          >
            <v-icon>delete</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-expansion-panel-content>
    <!-- Delete dialog -->
    <alert-dialog
      :is-open="isDeleteDialogOpen"
      :ok-btn-text="$t('actions.delete')"
      @onOk="deleteLinePricingGroupHandler(true)"
      @onCancel="deleteLinePricingGroupHandler(false)"
    >
      <template v-slot:header>{{ $t('dialogs.deleteLinePricingGroup.header') }}</template>
      <template v-slot:body>
        {{ $t('dialogs.deleteLinePricingGroup.body') }}
        <strong>{{ linePricingGroup ? linePricingGroup.linePricedGroupDescription : '' }}</strong>
      </template>
    </alert-dialog>
  </v-expansion-panel>
</template>

<script>
import { get, map, isEqual, isNumber, groupBy, find } from 'lodash';
import { mapGetters, mapState } from 'vuex';
import { truncationLength } from '@sharedModules/config/products-grid';
import getPriceFieldMixin from '../../../../mixins/getPriceField';
import numberFormats from '@enums/number-formats';
import featureFlagsMixin from '../../../../mixins/featureFlags';
import tablesMixin from '../../../../mixins/tables';
import { useZones } from '@enums/feature-flags';

export default {
  mixins: [getPriceFieldMixin, featureFlagsMixin, tablesMixin],
  props: {
    linePricingGroup: {
      type: Object,
      required: true,
    },

    disabled: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      rules: {
        linePriceFactorRules: [this.valueRequired, this.valueIsPositive],
        missingDescription: [this.valueRequired],
      },
      pagination: {
        sortBy: ['productKeyDisplay'],
        itemsPerPage: -1,
        descending: false,
      },
      articleHeaders: [
        {
          text: this.$t('settings.architecture.productKey'),
          sortable: true,
          value: 'productKeyDisplay',
        },
        {
          text: this.$t('settings.architecture.toolStoreGroup'),
          sortable: true,
          value: 'toolStoreGroupDescription',
          featureFlag: useZones,
        },
        {
          text: this.$t('settings.architecture.productDescription'),
          sortable: true,
          value: 'description',
        },
        {
          text: this.$t('settings.architecture.productSize'),
          value: 'size',
          sortable: true,
        },
        {
          text: this.$t('settings.architecture.packageTypeDescription'),
          value: 'packageTypeDescription',
          sortable: true,
        },
      ],
      linePricedHeaders: [
        {
          text: this.$t(
            'settings.setArchitectureRules.architectureDriversRules.productTable.subGroup'
          ),
          sortable: true,
          value: 'subGroup',
        },
        {
          text: this.$t('settings.architecture.LinePriceFactorHeader'),
          value: 'linePriceFactor',
        },
      ],
      truncationLength,
      isDeleteDialogOpen: false,
      numberFormats,
      useZones,
    };
  },

  computed: {
    ...mapGetters('architectureGroup', ['hasMultipleSubGroups']),
    ...mapState('filters', ['selectedPriceType']),
    ...mapState('storeGroupRelationships', ['storegroupKeyNameMap']),
    ...mapState('clientConfig', ['releaseFlags']),

    subGroupValidationError() {
      return this.hasMultipleSubGroups(this.linePricingGroup.products);
    },

    linePricingGroupProducts() {
      const products = this.linePricingGroup.products;
      this.enrichProductsWithManualAnchorStates(products);
      this.enrichProductsWithSubGroupAndKey(products);
      return products;
    },

    filteredArticlesHeaders() {
      return this.filterArrayByFeatureFlag(this.articleHeaders);
    },

    isManualAnchorEnabled() {
      return this.releaseFlags.enableManualAnchorInLinePricing;
    },

    headers() {
      const dynamicHeaders = [
        {
          text: this.$t('settings.architecture.costPrice'),
          sortable: true,
          value: `${this.getPriceFieldObjectPath('intentionCost')}`,
        },
        {
          text: this.$t('settings.architecture.livePrice'),
          sortable: true,
          value: `${this.getPriceFieldObjectPath('livePrice')}`,
        },
      ];
      let manualAnchorHeader = [];
      if (this.isManualAnchorEnabled) {
        manualAnchorHeader = [
          {
            text: this.$t('settings.architecture.manualAnchorHeader'),
            sortable: false,
            value: 'isManualAnchor',
          },
        ];
      }

      const headers = [
        ...manualAnchorHeader,
        ...this.filteredArticlesHeaders,
        ...dynamicHeaders,
        ...this.linePricedHeaders,
      ];
      return headers;
    },
  },

  methods: {
    deleteProduct(linePricedProduct) {
      this.$emit('deleteLinePricedProduct', { linePricedProduct });
    },

    deleteLinePricingGroup() {
      this.$emit('deleteLinePricingGroup', { linePricingGroup: this.linePricingGroup });
    },

    deleteLinePricingGroupHandler(shouldDelete = false) {
      if (shouldDelete) {
        this.deleteLinePricingGroup();
      }

      this.isDeleteDialogOpen = false;
    },

    preventExpand(event) {
      event.preventDefault();
      event.stopPropagation();
    },

    valueRequired(v) {
      return !!v || v === 0 || this.$t('settings.parameters.errors.fieldInvalid');
    },

    valueIsPositive(v) {
      return v > 0 || this.$t('settings.parameters.errors.fieldInvalid');
    },

    openDeleteDialog() {
      this.isDeleteDialogOpen = true;
    },

    changeSort(column) {
      if (isEqual(this.pagination.sortBy, [column])) {
        this.pagination.descending = !this.pagination.descending;
        return;
      }
      this.pagination.sortBy = [column];
      this.pagination.descending = false;
    },

    getLivePrice(item) {
      const field = this.getPriceFieldObjectPath('livePrice');
      return get(item, field);
    },

    getIntentionCost(item) {
      const field = this.getPriceFieldObjectPath('intentionCost');
      return get(item, field);
    },

    /**
     * Enriches products with manual anchor states by grouping them and determining
     * which products should be disabled for manual anchor column based on manual anchor selection.
     *
     * This method groups products by their toolStoreGroupKey, then for each group:
     * - Finds the product that is marked as a manual anchor (if any)
     * - Sets the `isDisabledForManualAnchor` property on all products in the group
     * - Products are disabled if there's a manual anchor and they're not the anchor themselves
     *
     * @param {Array<Object>} products - Array of product objects to process
     * @param {string} products[].toolStoreGroupKey - Key used to group products together
     * @param {string} products[].productKey - Unique identifier for the product
     * @param {boolean} products[].isManualAnchor - Whether this product is set as manual anchor
     *
     * @modifies {Object} products - Adds `isDisabledForManualAnchor` property to each product
     */
    enrichProductsWithManualAnchorStates(products) {
      // Groups products by their toolStoreGroupKey
      const grouped = groupBy(products, 'toolStoreGroupKey');

      // Process each group to set manual anchor states
      Object.values(grouped).forEach(group => {
        const manualAnchor = find(group, 'isManualAnchor');
        group.forEach(product => {
          product.isDisabledForManualAnchor = manualAnchor
            ? product.productKey !== manualAnchor.productKey
            : false;
        });
      });
    },

    formatSubGroup(product) {
      const subGroupValues = get(product, 'subGroups', []);

      return subGroupValues
        .map(value => (isNumber(value) ? this.formatNumber({ number: value }) : value))
        .join('-');
    },

    /**
     * Enriches products with subGroup and productToolStoreGroupKey.
     * Adds a subGroup property to each product based on its subGroupsValues.
     * Also adds a productToolStoreGroupKey property for unique identification.
     *
     * @param {Array<Object>} products - Array of product objects to process
     * @returns {Array<Object>} - Array of enriched product objects
     */
    enrichProductsWithSubGroupAndKey(products) {
      map(products, product => {
        product.subGroup = this.formatSubGroup(product);
        product.productToolStoreGroupKey = `${product.productKey}::${product.toolStoreGroupKey}`;
        return product;
      });
    },

    updateManualAnchorStates(item) {
      // at most one product can be selected as manual anchor in each tool store group
      // if a manual anchor is selected, all other products in the same tool store group must be disabled
      if (item.isManualAnchor) {
        this.linePricingGroupProducts.forEach(product => {
          if (
            product.toolStoreGroupKey === item.toolStoreGroupKey &&
            product.productKey !== item.productKey
          ) {
            product.isDisabledForManualAnchor = true;
          }
        });
      } else {
        // if a manual anchor is deselected, all products in the same tool store group are enabled to allow selection
        this.linePricingGroupProducts.forEach(product => {
          if (product.toolStoreGroupKey === item.toolStoreGroupKey) {
            product.isDisabledForManualAnchor = false;
          }
        });
      }
    },
  },
};
</script>

<style lang="scss">
@import '@style/base/_variables.scss';
$line-price-factor-min-width: 120px;

.manual-anchor-checkbox {
  display: flex;
  justify-content: center;
  align-items: center;

  &.checkbox-disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}
.validation-fail {
  color: red;
  font-size: 1.4rem;
  vertical-align: middle;
}

.theme--light.line-priced-products.v-data-table {
  border-bottom: 0rem;

  .v-data-table__wrapper {
    position: relative;
  }

  table {
    th {
      &.line-price-factor-header {
        min-width: $line-price-factor-min-width;
      }
    }
    tbody {
      td {
        &.line-price-factor {
          padding: 0;
          width: 5rem;
          background: $pricing-background;

          .line-price-factor-input {
            min-width: $line-price-factor-min-width;
            input {
              background: $pricing-white;
            }
          }

          .v-text-field__details {
            position: absolute;
            right: 0;
          }

          .v-input {
            padding-top: 0;
            height: 2.2rem;
            font-size: 1.2rem;

            .v-text-field__slot input {
              padding: 0;
              padding-left: 0.5rem;
            }
          }
        }
      }
    }
  }
}

.v-expansion-panel--active > .line-pricing-group.v-expansion-panel-header {
  border-bottom: 0.1rem solid #9b9b9b;
  margin-bottom: 1.5rem;
}
</style>
